import useUser from "../../../api/api-hooks/auth/useUser";
import AdminNavbarNoModule from "../../../components/Navbars/AdminNavbarNoModule";
import { ChangeEvent, useMemo } from "react";
import { useEffect, useState } from "react";
import _ from "underscore";
import {
  Alert,
  Button,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  Row,
} from "reactstrap";
import Constants from "../../../utils/Constants";
import { deleteObjectProperties } from "../../../utils/GeneralUtils";
import useCreateCloud from "../../../api/api-hooks/cloud/useCreateCloud";
import CloudNameGroup from "./CreateCloudGroups/CloudNameGroup";
import useDefaultPermissions from "../../../api/api-hooks/cloud/useDefaultPermissions";
import AppSpinner from "../../../elements/AppSpinner";
import AppHelmet from "../../../components/AppComponents/AppHelmet";
import IconButton from "../../../elements/IconButton";
import CloudUsersGroup from "./CreateCloudGroups/CloudUsersGroup";
import Cookies from "js-cookie";
import useInviteUser from "../../../api/api-hooks/tenantUsers/useInviteUser";
import useCloud from "../../../api/api-hooks/cloud/useCloud";
import { useAtom } from "jotai";
import { addUserModal } from "../../../atoms/GlobalAtoms";
import useRemoveUser from "../../../api/api-hooks/tenantUsers/useRemoveUser";
import useTenantUsers from "../../../api/api-hooks/tenantUsers/useTenantUsers";
import { getUserCloudPermissions } from "../../../utils/UserUtils";
import { useNavigate } from "react-router-dom";

export const createCloud = ({
  values,
  error,
  setError,
  ownerEmail,
  createCloudAPI,
  openCloud,
  onCreateCloudSuccess,
  isSupportal = false,
}: any) => {
  const missingFields = [];

  for (const key of Object.keys(values)) {
    if (!values[key] && ["CloudName", "CloudUsers"].includes(key)) {
      missingFields.push(key.replace(/([a-z])([A-Z])/g, "$1 $2"));
    }
  }

  if (missingFields.length > 0) {
    setError(
      `Please fill in the following fields: ${missingFields.join(", ")}`
    );
    return;
  }

  let newBillingAddress = values.BillingAddress ?? "";

  if (newBillingAddress) {
    if (values.BillingAddress2) {
      newBillingAddress += `, ${values.BillingAddress2}`;
    }

    newBillingAddress += `, ${values.BillingCity}`;
    newBillingAddress += ` ${values.BillingState}`;
    newBillingAddress += `, ${values.BillingZip}`;
  }

  const payload = {
    ...values,
    BillingAddress: newBillingAddress,
    OwnerEmail: ownerEmail,
  };

  // Now that we have formed the address, delete the partial address fields
  deleteObjectProperties(payload, [
    "BillingAddress2",
    "BillingCity",
    "BillingState",
    "BillingZip",
  ]);

  error && setError(null);

  createCloudAPI({
    payload: payload,
    openCloud: openCloud,
    onSuccess: onCreateCloudSuccess,
    isSupportal: isSupportal,
  });
};

const initialValues = (currentUser: any, ownerPermissionID: string) => {
  return {
    Tier: "Team",
    Name: "",
    CloudUsers: [
      {
        PersonID: currentUser?.id,
        Email: currentUser?.PrimaryContactEmail,
        TenantOwner: true,
        PermissionsID: ownerPermissionID,
      },
    ],
    SampleData: false,
  };
};

const Wrapper = ({ children }: { children: React.ReactNode }) => (
  <Row className="justify-content-center">
    <Col lg={"9"} xl={"8"} md={"12"}>
      {children}
    </Col>
  </Row>
);

const CreateCloudView = () => {
  const [values, setValues] = useState<any>(null);
  const [error, setError] = useState(null);
  const { defaultPermissions } = useDefaultPermissions();
  const { user, userLoading } = useUser();
  const [_modalAtom, setModalAtom] = useAtom(addUserModal);

  const {
    createCloud: createCloudAPI,
    creatingCloud,
    createCloudError,
  } = useCreateCloud({ isSupportal: false });
  const navigate = useNavigate();

  const onCreateCloud = () => {
    createCloud({
      values,
      error,
      setError,
      createCloudAPI,
      ownerEmail: user.PrimaryContactEmail,
      openCloud: true,
      onCreateCloudSuccess: () => {
        setModalAtom(true);
      },
    });
  };

  const ownerPermissions = useMemo(() => {
    if (!defaultPermissions?.length) return null;

    return _.findWhere(defaultPermissions, { Type: "Cloud", Owner: true });
  }, [defaultPermissions]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (!Cookies.get("authenticationToken")) {
      navigate("/login");
    }
  }, []);

  useEffect(() => {
    if (values !== null) return;

    if (userLoading || !ownerPermissions) return;

    setValues(initialValues(user, ownerPermissions.DefaultID));
  }, [user, userLoading, ownerPermissions, values]);

  if (values === null) return null;

  if (creatingCloud) {
    return (
      <>
        <AppHelmet title="Creating Your GrowthCloud - GrowthCloud.com" />
        <div className="main-content">
          {/*  @ts-ignore  */}
          <AdminNavbarNoModule
            /* @ts-ignore */
            theme="dark"
            /* @ts-ignore */
            module={Constants.MODULES.SUPPORTAL}
          />
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "100vh" }}
          >
            <div
              className="border border-superlight shadow-sm rounded d-flex flex-column align-items-center p-5"
              style={{ maxWidth: 400 }}
            >
              <h1 className="mb-4">Creating Cloud...</h1>
              <AppSpinner
                style={{ height: "3.5rem", width: "3.5rem", fontSize: 25 }}
                className="mt-4 mb-5"
                size="xl"
              />
              <div style={{ fontWeight: 500 }} className="text-center">
                We&apos;re creating your new GrowthCloud instance. This will
                only take a minute or two.
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AppHelmet title="Create A New Cloud - GrowthCloud.com" />
      <div style={{ height: "100vh" }}>
        <Button
          outline
          className="m-2"
          size="sm"
          onClick={() => navigate("/clouds")}
        >
          <i className="mdi mdi-arrow-left" />
          <span className="btn-inner--text">Back</span>
        </Button>
        <Container className="mt-2">
          <Wrapper>
            <div className="text-center mt-4 mb-5">
              <h1 className="text-secondary">Cloud Name</h1>
              <p className="text-lead text-secondary">
                Select a GrowthCloud hosting tier
              </p>
            </div>
            <CloudNameGroup values={values} onChange={onChange} />
            <FormGroup check>
              <Label
                className="cursor-pointer pt-3"
                style={{ fontSize: "14px" }}
                check
              >
                <Input
                  type="checkbox"
                  onChange={(e) =>
                    setValues((prev: any) => ({
                      ...prev,
                      SampleData: e.target.checked,
                    }))
                  }
                />{" "}
                Load sample data
              </Label>
            </FormGroup>

            <hr />
          </Wrapper>

          {createCloudError && (
            <Wrapper>
              <Alert color="danger">{createCloudError}</Alert>
            </Wrapper>
          )}
          <Row className="d-flex flex-column align-items-center justify-content-center mt-2 pb-6">
            <div style={{ maxWidth: "300px" }} className="d-flex flex-column">
              <Button
                size="md"
                className="px-5 mb-2"
                color="primary"
                onClick={onCreateCloud}
                /* @ts-ignore */
                disabled={!values?.Name || creatingCloud}
              >
                {creatingCloud ? <AppSpinner color="white" /> : "Create Cloud"}
              </Button>
            </div>
            <small className="text-light">
              By clicking
              {values?.Tier === "Team" ? '"Create Cloud"' : '"Request Access"'},
              you agree to our Terms of Service and Privacy Policy
            </small>
          </Row>
          <StatusModal open={creatingCloud} />
        </Container>
      </div>
    </>
  );
};

export const StatusModal = ({ open }: { open: boolean }) => {
  return (
    <Modal isOpen={open} centered>
      <div className="text-center px-5 pb-4 pt-2">
        <h1>Creating Cloud</h1>
        <p>
          We are creating your new GrowthCloud instance. This will only take a
          minute or two.
        </p>
        <AppSpinner
          color="primary"
          size="xl"
          style={{ height: 120, width: 120, fontSize: 40 }}
        ></AppSpinner>
      </div>
    </Modal>
  );
};

export const OnBoardModal = ({
  open,
  tenantID,
  setModal,
}: {
  open: boolean;
  tenantID: string;
  setModal: (data: boolean) => void;
}) => {
  const { defaultPermissions } = useDefaultPermissions();
  const { inviteUser } = useInviteUser();
  const { cloudPermissions } = useCloud(tenantID);
  const { removeUser: removeUserAPI } = useRemoveUser();
  const { users } = useTenantUsers();
  const { user, cPermissions } = useUser();

  const [values, setValues] = useState<any>();
  const ownerPermissions = useMemo(() => {
    if (!defaultPermissions?.length) return null;

    return _.findWhere(defaultPermissions, { Type: "Cloud", Owner: true });
  }, [defaultPermissions]);

  useEffect(() => {
    if (!users || !user || !ownerPermissions || !cPermissions || !tenantID) {
      return;
    }

    const values = initialValues(user, ownerPermissions.DefaultID);
    const cloudUsers = users?.map((user: any) => {
      let cloudUser: any = {};
      const permissionProfile = getUserCloudPermissions(
        user,
        cPermissions,
        tenantID
      );
      cloudUser.Email = user?.PrimaryContactEmail;
      // @ts-ignore
      cloudUser.PermissionsID = permissionProfile?.DefaultID;
      // @ts-ignore
      cloudUser.TenantOwner = permissionProfile?.Owner || false;
      cloudUser.Name = user?.Name;
      cloudUser.id = user?._id;
      return cloudUser;
    });

    setValues({ ...values, CloudUsers: cloudUsers });
  }, [users, user, ownerPermissions, cPermissions, tenantID]);

  return (
    <Modal isOpen={open} size="lg">
      <div style={{ width: "100%" }} className="d-flex justify-content-end p-2">
        <IconButton
          icon="mdi mdi-close"
          onClick={() => setModal(false)}
        ></IconButton>
      </div>
      <div
        style={{ width: "100%", height: "100%" }}
        className="d-flex justify-content-center align-items-center flex-column"
      >
        <h1>Onboard Your Team</h1>
        <p>Invite them to your new GrowthCloud</p>
        <div style={{ width: "100%" }} className="px-5 pb-4">
          {values && (
            <CloudUsersGroup
              values={values}
              onChange={handleAddUser}
              currentUser={user}
              removeUser={removeUser}
              defaultPermissions={defaultPermissions}
              useExistingUsers
            />
          )}
        </div>
        <div className="mb-4">
          <Button
            onClick={() => setModal(false)}
            size="sm"
            style={{ minWidth: 100 }}
            color={values?.CloudUsers?.length > 1 ? "primary" : "secondary"}
          >
            {values?.CloudUsers?.length > 1 ? "Finish" : "Skip This Step"}
          </Button>
        </div>
      </div>
    </Modal>
  );

  function removeUser(email: string) {
    const userFound = values.CloudUsers.find((u: any) => u.Email === email);
    if (userFound)
      removeUserAPI({
        user: { ...userFound },
        supportTargetCloudID: undefined, // This is NOT a support request.
        onSuccess: () => {
          setValues((prev: any) => {
            return {
              ...prev,
              CloudUsers: prev.CloudUsers.filter((u: any) => u.Email !== email),
            };
          });
        },
      });
  }

  function handleAddUser(data: any) {
    const invitedUser = data.target.invitedUser;
    const permission = cloudPermissions.find(
      (p) => p.DefaultID === invitedUser.PermissionsID
    )?._id;
    if (!permission) return;

    inviteUser({
      email: invitedUser.Email,
      permission: permission,
      supportTargetCloudID: undefined, // This is NOT a support request.
    });
  }
};

export default CreateCloudView;
